import { useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { Capacitor } from "@capacitor/core";
import { SplashScreen } from "@capacitor/splash-screen";
import splashLogo from "@/assets/berastays-logo-splash.png";
import { preloadMapboxToken } from "@/components/PropertyMapOptimized";

interface OptimizedSplashProps {
  onComplete: () => void;
}

/**
 * OptimizedSplash - Splash screen otimizada para transição fluida nativo → React
 * 
 * Fluxo:
 * 1. Native Splash (Android 12+) é exibido pelo sistema
 * 2. WebView inicializa com background brand color (#E8513D)
 * 3. React Splash aparece imediatamente (mesma cor/fundo)
 * 4. Assets pré-carregados em paralelo
 * 5. Splash nativo é escondido quando React está pronto
 * 6. Fade out suave para o app
 */
export const OptimizedSplash = ({ onComplete }: OptimizedSplashProps) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isReady, setIsReady] = useState(false);
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const isNative = useMemo(() => Capacitor.isNativePlatform(), []);
  const startedAt = useMemo(() => Date.now(), []);

  useEffect(() => {
    let cancelled = false;
    let nativeSplashHidden = false;

    const hideNativeSplash = async () => {
      if (isNative && !nativeSplashHidden) {
        try {
          // Pequeno delay para garantir que o React Splash está renderizado
          await new Promise(r => setTimeout(r, 100));
          await SplashScreen.hide({ fadeOutDuration: 0 });
          nativeSplashHidden = true;
          console.log('[OptimizedSplash] Native splash hidden');
        } catch (e) {
          console.log('[OptimizedSplash] Error hiding native splash:', e);
        }
      }
    };

    const preloadCriticalData = async () => {
      // Paralelização de tarefas críticas
      const tasks = [];

      // Pré-carregar imagens (apenas web)
      if (!isNative) {
        tasks.push(
          supabase
            .from('properties')
            .select('images')
            .eq('status', 'active')
            .limit(4)
            .then(({ data }) => {
              if (data) {
                data.forEach((p: any) => {
                  if (p.images?.[0]) {
                    const img = new Image();
                    img.src = p.images[0];
                  }
                });
              }
            })
        );
      }

      // Warmup queries
      tasks.push(
        queryClient.prefetchQuery({
          queryKey: ["platform-settings"],
          queryFn: async () => {
            const { data } = await supabase
              .from("platform_settings")
              .select("value")
              .eq("key", "checkout_banner_text")
              .single();
            return data?.value || null;
          },
          staleTime: 1000 * 60 * 5,
        }).catch(() => {})
      );

      tasks.push(
        queryClient.prefetchQuery({
          queryKey: ["explorar-properties"],
          queryFn: async () => {
            const { data } = await supabase
              .from("properties")
              .select("id, title, price_per_night, images, location, property_type")
              .eq("status", "active")
              .eq("is_verified", true)
              .limit(6);
            return data || [];
          },
          staleTime: 1000 * 30,
        }).catch(() => {})
      );

      // Pré-aquecer roles do utilizador autenticado para evitar flash da vista de hóspede em proprietários
      if (user?.id) {
        tasks.push(
          queryClient.prefetchQuery({
            queryKey: ["user-roles", user.id],
            queryFn: async () => {
              const { data, error } = await supabase
                .from("user_roles")
                .select("role")
                .eq("user_id", user.id);
              if (error) return [];
              return (data || []).map((r: any) => r.role);
            },
            staleTime: 1000 * 60 * 5,
          }).catch(() => {})
        );
      }

      // Preload heavy JS chunks in background while splash covers the screen
      tasks.push(import("@/pages/FazerOferta").catch(() => {}));
      tasks.push(preloadMapboxToken().catch(() => {}));


      await Promise.allSettled(tasks);
    };

    const waitMinimumTime = async () => {
      const minMs = isNative ? 1500 : 1500; // Mínimo 1.5s para pré-carregar sem parecer lento
      const elapsed = Date.now() - startedAt;
      const remaining = minMs - elapsed;
      if (remaining > 0) {
        await new Promise(r => setTimeout(r, remaining));
      }
    };

    const initialize = async () => {
      console.log('[OptimizedSplash] Starting initialization...');

      // Passo 1: Esconder splash nativo imediatamente após primeiro frame React
      await hideNativeSplash();

      // Passo 2: Marcar como ready (inicia animações visuais)
      if (!cancelled) {
        setIsReady(true);
      }

      // Passo 3: Pré-carregar dados enquanto splash está visível
      await Promise.allSettled([
        preloadCriticalData(),
        waitMinimumTime()
      ]);

      // Passo 4: Fechar splash React e navegar
      if (!cancelled) {
        console.log('[OptimizedSplash] Closing splash, navigating...');
        setIsVisible(false);
        
        setTimeout(() => {
          if (cancelled) return;
          
          onComplete();
          
          // Navegação baseada em estado de auth
          const targetPath = user ? "/explorar" : "/auth?mode=login";
          console.log('[OptimizedSplash] Navigating to:', targetPath);
          navigate(targetPath, { replace: true });
        }, 300); // Tempo para fade out
      }
    };

    // Iniciar após primeiro paint
    requestAnimationFrame(() => {
      initialize();
    });

    // Timeout de segurança máximo 5s
    const safetyTimeout = setTimeout(() => {
      if (!cancelled && isVisible) {
        console.log('[OptimizedSplash] Safety timeout reached');
        setIsVisible(false);
        onComplete();
        navigate(user ? "/explorar" : "/auth?mode=login", { replace: true });
      }
    }, 5000);

    return () => {
      cancelled = true;
      clearTimeout(safetyTimeout);
    };
  }, [isNative, onComplete, navigate, queryClient, startedAt, user]);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 1 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ 
            duration: 0.25, 
            ease: [0.4, 0, 0.2, 1] // Ease out suave
          }}
          className="fixed inset-0 z-[99999] flex flex-col items-center justify-center overflow-hidden"
          style={{
            background: "linear-gradient(135deg, #E8513D 0%, #D94F30 40%, #C44228 100%)",
            // Força GPU layer para evitar flicker
            transform: "translateZ(0)",
            willChange: "opacity"
          }}
        >
          {/* Logo com animação suave */}
          <motion.div
            initial={{ scale: 0.85, opacity: 0 }}
            animate={{ 
              scale: isReady ? 1 : 0.9, 
              opacity: 1 
            }}
            transition={{ 
              duration: 0.5, 
              ease: [0.25, 0.46, 0.45, 0.94] // Ease out cubic
            }}
            className="relative"
            style={{ transform: "translateZ(0)" }}
          >
            {/* Glow effect */}
            <motion.div
              animate={{ 
                opacity: [0.15, 0.35, 0.15],
                scale: [0.95, 1.05, 0.95]
              }}
              transition={{ 
                duration: 2.5, 
                repeat: Infinity, 
                ease: "easeInOut" 
              }}
              className="absolute inset-0 -m-10 rounded-full blur-3xl"
              style={{ backgroundColor: "rgba(255, 200, 150, 0.3)" }}
            />
            
            {/* Logo image */}
            <img
              src={splashLogo}
              alt="BeraStays"
              className="relative w-36 sm:w-40 h-auto max-h-36 sm:max-h-40 object-contain drop-shadow-2xl"
              style={{ 
                imageRendering: "-webkit-optimize-contrast",
                transform: "translateZ(0)"
              }}
              // Previne flash de imagem não carregada
              onLoad={(e) => {
                (e.target as HTMLImageElement).style.opacity = "1";
              }}
            />
          </motion.div>

          {/* Tagline */}
          <motion.h2
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: isReady ? 1 : 0, y: isReady ? 0 : 10 }}
            transition={{ delay: 0.2, duration: 0.4 }}
            className="text-lg sm:text-xl font-semibold tracking-tight text-center mt-5"
            style={{ color: "rgba(255, 255, 255, 0.95)" }}
          >
            África Vista de Perto
          </motion.h2>

          {/* Progress bar indicador */}
          <motion.div
            initial={{ opacity: 0, scaleX: 0 }}
            animate={{ opacity: 1, scaleX: 1 }}
            transition={{ delay: 0.3, duration: 0.3 }}
            className="w-44 mt-6 origin-left"
          >
            <div 
              className="h-0.5 rounded-full overflow-hidden" 
              style={{ backgroundColor: "rgba(255, 255, 255, 0.2)" }}
            >
              <motion.div
                className="h-full rounded-full"
                style={{ backgroundColor: "rgba(255, 255, 255, 0.9)" }}
                initial={{ width: "0%" }}
                animate={{ width: "100%" }}
                transition={{ 
                  duration: 2.2, 
                  ease: [0.4, 0, 0.2, 1] // Ease in-out
                }}
              />
            </div>
          </motion.div>

          {/* Footer branding */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            transition={{ delay: 1, duration: 0.5 }}
            className="absolute bottom-8 text-[10px] font-medium tracking-[0.25em] uppercase"
            style={{ color: "rgba(255, 255, 255, 0.4)" }}
          >
            Beracha Tech
          </motion.p>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default OptimizedSplash;
